import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Modal from "src/components/ui/Modal";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Checkbox from "src/components/ui/Checkbox";
import Button from "src/components/ui/Button";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as planActions from "src/redux/slices/planSlice";
import { Plan, PlanFeatures } from "src/types/plan.types";

const DEFAULT_FEATURES: PlanFeatures = {
  totalUsers: 1,
  totalProducts: 10,
  totalSaleOrders: 10,
  totalPurchaseOrders: 10,
  totalAdjustmentOrders: 10,
  totalSuppliers: 1,
};

interface PlanFormModalProps {
  open: boolean;
  onClose: () => void;
  // Omit for create mode; pass the plan being edited for edit mode.
  plan?: Plan | null;
}

// Shared by both "Add Plan" (list page) and "Edit Plan" (list page's pencil
// action, and the /admin/plans/:planId/edit route) so the two flows never
// drift apart again.
const PlanFormModal: React.FC<PlanFormModalProps> = ({
  open,
  onClose,
  plan,
}) => {
  const isEdit = Boolean(plan?._id);
  const dispatch = useAppDispatch();
  const { createPlan, editPlan } = useAppSelector((state) => state.plan);
  const saveState = isEdit ? editPlan : createPlan;

  const [planName, setPlanName] = useState("");
  const [planCode, setPlanCode] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [durationInDays, setDurationInDays] = useState("");
  const [showOnWebsite, setShowOnWebsite] = useState(false);
  const [features, setFeatures] = useState<PlanFeatures>(DEFAULT_FEATURES);

  const reset = () => {
    setPlanName("");
    setPlanCode("");
    setDescription("");
    setPrice("");
    setDurationInDays("");
    setShowOnWebsite(false);
    setFeatures(DEFAULT_FEATURES);
  };

  // Populate the form whenever a different plan is opened for editing, and
  // reset back to blank when the modal swings back to create mode.
  useEffect(() => {
    if (!open) return;
    if (plan) {
      setPlanName(plan.planName ?? "");
      setPlanCode(plan.planCode ?? "");
      setDescription(plan.description ?? "");
      setPrice(plan.price != null ? String(plan.price) : "");
      setDurationInDays(
        plan.durationInDays != null ? String(plan.durationInDays) : ""
      );
      setShowOnWebsite(Boolean(plan.showOnWebsite));
      setFeatures(plan.features ?? DEFAULT_FEATURES);
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?._id]);

  useEffect(() => {
    if (saveState.data) {
      toast.success(isEdit ? "Plan updated successfully" : "Plan created successfully");
      dispatch(isEdit ? planActions.resetEditPlan() : planActions.resetCreatePlan());
      reset();
      onClose();
    } else if (saveState.error) {
      toast.error(`Error: ${saveState.error}`);
      dispatch(isEdit ? planActions.resetEditPlan() : planActions.resetCreatePlan());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState]);

  const featureFields: { key: keyof PlanFeatures; label: string }[] = useMemo(
    () => [
      { key: "totalUsers", label: "Total Users" },
      { key: "totalProducts", label: "Total Products" },
      { key: "totalSaleOrders", label: "Total Sale Orders" },
      { key: "totalPurchaseOrders", label: "Total Purchase Orders" },
      { key: "totalAdjustmentOrders", label: "Total Adjustment Orders" },
      { key: "totalSuppliers", label: "Total Suppliers" },
    ],
    []
  );

  const isValid = planName.trim() && planCode.trim() && price !== "" && durationInDays !== "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    const payload = {
      planName: planName.trim(),
      planCode: planCode.trim(),
      description: description.trim(),
      price: Number(price),
      durationInDays: Number(durationInDays),
      features,
      showOnWebsite,
    };

    dispatch(
      isEdit
        ? planActions.editPlanRequest({ ...payload, _id: plan!._id })
        : planActions.createPlanRequest(payload)
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit Plan" : "Add Plan"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Plan Name"
            placeholder="Enter plan name"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            required
          />
          <Input
            label="Plan Code"
            placeholder="Enter plan code"
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Plan Description
          </label>
          <Textarea
            placeholder="Enter plan description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Plan Price"
            type="number"
            placeholder="Enter plan price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
          />
          <Input
            label="Duration (in days)"
            type="number"
            placeholder="Enter plan duration"
            value={durationInDays}
            onChange={(e) => setDurationInDays(e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-3">
          {featureFields.map((f) => (
            <Input
              key={f.key}
              label={f.label}
              type="number"
              value={features[f.key]}
              onChange={(e) =>
                setFeatures((prev) => ({
                  ...prev,
                  [f.key]: Number(e.target.value),
                }))
              }
            />
          ))}
        </div>

        <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
          <Checkbox
            label="Show on website"
            checked={showOnWebsite}
            onChange={(e) => setShowOnWebsite(e.target.checked)}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            When on, this plan appears on the public pricing page. Leave it
            off for internal-only plans (custom deals, trial, etc.).
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={saveState.loading}
            disabled={!isValid || saveState.loading}
          >
            {isEdit ? "Save Changes" : "Create Plan"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PlanFormModal;
